/**
 * Clinical Supervisor — QA Rubric Dashboard (Spec F + Spec G).
 *
 * Lets a clinical supervisor:
 *   1. Pick a service group + rubric → see aggregate scores across the group
 *      (Level 1 read; just averages, no per-call detail).
 *   2. Drill into a single call → request Level 2 (summary) by default,
 *      Level 3 (rationale + evidence) with a typed reason, or escalate to
 *      Level 4 (full transcript excerpt) via a break-glass grant request.
 *
 * Replaces the curl block in Phase 9 of the dev test guide.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Button, Chip, Alert,
  TextField, MenuItem, LinearProgress, Divider, Snackbar, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const RUBRICS = [
  { key: 'risk-safeguarding-v1', label: 'Risk & Safeguarding' },
  { key: 'therapeutic-alliance-v1', label: 'Therapeutic Alliance' },
  { key: 'modality-fidelity-cbt-v1', label: 'Modality Fidelity (CBT)' }
];

const LEVELS = [
  { value: 1, label: 'Level 1 — Aggregate only' },
  { value: 2, label: 'Level 2 — Summary scores' },
  { value: 3, label: 'Level 3 — Rationale + evidence (reason required)' },
  { value: 4, label: 'Level 4 — Full transcript excerpt (break-glass)' }
];

export default function SupervisorQaDashboard({ token }) {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [serviceGroupId, setServiceGroupId] = useState('');
  const [rubricKey, setRubricKey] = useState(RUBRICS[0].key);

  const [aggregate, setAggregate] = useState(null);
  const [scoredCalls, setScoredCalls] = useState([]);
  const [loadingAgg, setLoadingAgg] = useState(false);
  const [aggError, setAggError] = useState(null);

  const [drillCall, setDrillCall] = useState(null); // { liveCallId, requestedLevel, reason, data }
  const [drillBusy, setDrillBusy] = useState(false);
  const [drillError, setDrillError] = useState(null);

  const [breakGlassDialog, setBreakGlassDialog] = useState({ open: false, reason: '' });
  const [toast, setToast] = useState(null);

  function authConfig() {
    return { headers: { 'x-auth-token': token } };
  }

  // Load service groups on mount (admin route — but supervisors get a pruned
  // list of their own groups via a similar endpoint; use the admin route as
  // fallback).
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/service-groups`, authConfig());
        const groups = res.data?.data || res.data || [];
        setServiceGroups(Array.isArray(groups) ? groups : []);
      } catch (err) {
        // Non-fatal; the supervisor can still type a serviceGroupId in.
        console.warn('Could not load service groups:', err?.message);
      }
    })();
  }, [token]);

  async function loadAggregate() {
    if (!serviceGroupId || !rubricKey) {
      setAggError('Pick a service group and a rubric first.');
      return;
    }
    setLoadingAgg(true);
    setAggError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/supervisor/qa/scores?rubricKey=${encodeURIComponent(rubricKey)}&serviceGroupId=${encodeURIComponent(serviceGroupId)}`,
        authConfig()
      );
      setAggregate(res.data?.aggregate || null);
      // Aggregate doesn't return per-call rows by default; keep scoredCalls
      // separate so future Level 2 reads can populate them per-call.
      setScoredCalls(res.data?.aggregate?.calls || res.data?.aggregate?.perCall || []);
    } catch (err) {
      setAggError(err?.response?.data?.error || err?.message || 'Could not load aggregate');
    } finally {
      setLoadingAgg(false);
    }
  }

  async function drillIntoCall(liveCallId, requestedLevel = 2, reason = '') {
    setDrillBusy(true);
    setDrillError(null);
    try {
      const params = new URLSearchParams({ rubricKey, requestedLevel: String(requestedLevel) });
      if (reason) params.append('reason', reason);
      const res = await axios.get(
        `${API_BASE_URL}/supervisor/qa/case/${encodeURIComponent(liveCallId)}?${params.toString()}`,
        authConfig()
      );
      setDrillCall({
        liveCallId,
        requestedLevel,
        reason,
        data: res.data
      });
    } catch (err) {
      setDrillError(err?.response?.data?.error || err?.message || 'Could not load call detail');
    } finally {
      setDrillBusy(false);
    }
  }

  async function escalateToLevel(level, reason) {
    if (!drillCall?.liveCallId) return;
    if (level >= 3 && (!reason || reason.trim().length < 10)) {
      setDrillError('Reason ≥10 characters is required for Level 3+');
      return;
    }
    await drillIntoCall(drillCall.liveCallId, level, reason);
  }

  async function requestBreakGlass() {
    if (!drillCall?.liveCallId) return;
    if (!breakGlassDialog.reason.trim() || breakGlassDialog.reason.trim().length < 10) {
      setDrillError('Break-glass reason ≥10 characters is required');
      return;
    }
    setDrillBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/supervisor/access/break-glass/request`,
        {
          targetType: 'qa_review',
          targetId: drillCall.liveCallId,
          requestedLevel: 4,
          reason: breakGlassDialog.reason.trim()
        },
        authConfig()
      );
      setToast('Break-glass grant active. Re-querying at Level 4…');
      setBreakGlassDialog({ open: false, reason: '' });
      await drillIntoCall(drillCall.liveCallId, 4, breakGlassDialog.reason.trim());
    } catch (err) {
      setDrillError(err?.response?.data?.error || err?.message || 'Break-glass request failed');
    } finally {
      setDrillBusy(false);
    }
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>QA Rubric Review</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Privacy-preserving rubric scoring across your service group. Aggregate views are always available;
            per-call rationale + evidence requires a typed reason; full transcript needs a time-bounded break-glass grant.
          </Typography>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
              <TextField
                label="Service Group" select fullWidth required
                value={serviceGroupId}
                onChange={(e) => setServiceGroupId(e.target.value)}
                sx={{ minWidth: 240 }}
              >
                {serviceGroups.length === 0 && <MenuItem value="" disabled>No groups loaded</MenuItem>}
                {serviceGroups.map(g => (
                  <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Rubric" select fullWidth required
                value={rubricKey}
                onChange={(e) => setRubricKey(e.target.value)}
                sx={{ minWidth: 240 }}
              >
                {RUBRICS.map(r => (
                  <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={loadAggregate}
                disabled={loadingAgg || !serviceGroupId}
                startIcon={<RefreshIcon />}
              >
                Load aggregate
              </Button>
            </Stack>
            {aggError && <Alert severity="error" sx={{ mt: 2 }}>{aggError}</Alert>}
            {loadingAgg && <LinearProgress sx={{ mt: 2 }} />}
          </CardContent>
        </Card>

        {aggregate && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Aggregate — {RUBRICS.find(r => r.key === rubricKey)?.label || rubricKey}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                {/* The API sends avgWeightedTotal and criticalCallCount; this read
                    averageWeightedTotal and criticalFlagCount, so the mean never
                    showed and every therapist read 0/100 (found with PD-09). */}
                <Chip label={`Calls: ${aggregate.callCount ?? 0}`} />
                {aggregate.unscoredCallCount > 0 && (
                  <Chip variant="outlined" label={`${aggregate.unscoredCallCount} scoring failed, not in the mean`} />
                )}
                {typeof aggregate.avgWeightedTotal === 'number' && (
                  <Chip
                    color={aggregate.avgWeightedTotal >= 70 ? 'success' : aggregate.avgWeightedTotal >= 50 ? 'warning' : 'error'}
                    label={`Mean: ${Math.round(aggregate.avgWeightedTotal)}/100`}
                  />
                )}
                {typeof aggregate.criticalCallCount === 'number' && (
                  <Chip color={aggregate.criticalCallCount > 0 ? 'error' : 'default'} label={`${aggregate.criticalCallCount} calls with critical flags`} />
                )}
              </Stack>

              {aggregate.perTherapist && aggregate.perTherapist.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>By therapist</Typography>
                  <Stack divider={<Divider />} spacing={1}>
                    {aggregate.perTherapist.map((t) => (
                      <Stack key={t.therapistId} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{t.therapistName || t.therapistId}</Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip size="small" label={`${t.callCount} calls`} />
                          <Chip size="small" label={typeof t.avgWeightedTotal === 'number' ? `${Math.round(t.avgWeightedTotal)}/100` : 'not scored'} />
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              {scoredCalls.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Scored calls in window</Typography>
                  <Stack divider={<Divider />} spacing={1}>
                    {scoredCalls.map((c) => (
                      <Stack key={c.liveCallId} direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2">
                            Call {String(c.liveCallId).slice(-6)} — {c.scoredAt ? new Date(c.scoredAt).toLocaleString() : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.scoringFailed || typeof c.weightedTotal !== 'number'
                              ? 'Scoring failed (not scored)'
                              : `Weighted total: ${Math.round(c.weightedTotal)}`}
                            {c.criticalFlagCount > 0 ? ` · ${c.criticalFlagCount} critical` : ''}
                          </Typography>
                        </Box>
                        <Tooltip title="View detail (Level 2)">
                          <IconButton onClick={() => drillIntoCall(c.liveCallId, 2)} size="small">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {drillCall && (
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">
                  Call {String(drillCall.liveCallId).slice(-8)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`Granted Level ${drillCall.data?.grantedLevel ?? '?'}`}
                    color={drillCall.data?.grantedLevel >= 3 ? 'warning' : 'default'}
                  />
                  <Button size="small" onClick={() => setDrillCall(null)}>Close</Button>
                </Stack>
              </Stack>
              {drillError && <Alert severity="error" sx={{ mb: 2 }}>{drillError}</Alert>}
              {drillBusy && <LinearProgress sx={{ mb: 2 }} />}

              <DrillUI
                drill={drillCall}
                onEscalate={(lvl, reason) => escalateToLevel(lvl, reason)}
                onRequestBreakGlass={() => setBreakGlassDialog({ open: true, reason: '' })}
              />
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog open={breakGlassDialog.open} onClose={() => setBreakGlassDialog({ ...breakGlassDialog, open: false })} fullWidth maxWidth="sm">
        <DialogTitle>Break-glass — Level 4 access</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Level 4 returns the full transcript excerpt for clinical supervision purposes. The grant is time-bounded
            (~30 min) and your access is recorded in the audit log along with this reason.
          </Alert>
          <TextField
            label="Reason for break-glass"
            placeholder="e.g. Critical risk flag — confirming clinical decision and safety planning"
            multiline minRows={3} fullWidth
            value={breakGlassDialog.reason}
            onChange={(e) => setBreakGlassDialog({ ...breakGlassDialog, reason: e.target.value })}
            sx={{ mt: 1 }}
            helperText="Minimum 10 characters."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBreakGlassDialog({ open: false, reason: '' })}>Cancel</Button>
          <Button
            variant="contained" color="warning" startIcon={<LockOpenIcon />}
            onClick={requestBreakGlass}
            disabled={drillBusy || breakGlassDialog.reason.trim().length < 10}
          >
            Confirm break-glass
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}

function DrillUI({ drill, onEscalate, onRequestBreakGlass }) {
  const [reason, setReason] = useState(drill?.reason || '');
  const score = drill?.data?.score;
  const granted = drill?.data?.grantedLevel ?? 0;
  const scope = drill?.data?.scope;

  if (!score) {
    return <Typography variant="body2" color="text.secondary">No score data returned.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip label={`scope: ${scope || 'unknown'}`} />
        {score.scoringFailed ? (
          <Chip label="Scoring failed (not scored)" variant="outlined" />
        ) : typeof score.weightedTotal === 'number' && (
          <Chip label={`Weighted: ${Math.round(score.weightedTotal)}/100`} />
        )}
        {(score.criticalFlags || []).length > 0 && (
          <Chip color="error" label={`${score.criticalFlags.length} critical flag${score.criticalFlags.length === 1 ? '' : 's'}`} />
        )}
      </Stack>

      {score.strengthsSummary && (
        <Typography variant="body2"><strong>Strengths:</strong> {score.strengthsSummary}</Typography>
      )}
      {score.improvementSummary && (
        <Typography variant="body2"><strong>For improvement:</strong> {score.improvementSummary}</Typography>
      )}

      {(score.dimensions || []).map((d) => (
        <Box key={d.key} sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'grey.300' }}>
          <Typography variant="body2" fontWeight={500}>
            {d.name} — {d.score === null || d.score === undefined ? 'not scored' : `${d.score}/${d.maxScore}`}
          </Typography>
          {d.rationale && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
              {d.rationale}
            </Typography>
          )}
          {d.evidenceExcerpts?.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {d.evidenceExcerpts.map((e, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                  “{e}”
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}

      <Divider />
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Escalate access</Typography>
        <TextField
          label="Reason (required for Level 3+)"
          placeholder="e.g. Reviewing critical risk flag for supervision"
          fullWidth multiline minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {[2, 3].map((lvl) => (
            <Button
              key={lvl}
              size="small"
              variant={granted === lvl ? 'contained' : 'outlined'}
              onClick={() => onEscalate(lvl, reason)}
              disabled={lvl >= 3 && reason.trim().length < 10}
            >
              {LEVELS.find(l => l.value === lvl)?.label}
            </Button>
          ))}
          <Button
            size="small" color="warning" variant="outlined"
            onClick={onRequestBreakGlass}
            startIcon={<LockOpenIcon fontSize="small" />}
          >
            Request break-glass (L4)
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
